import { useState, useRef, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import debouncePromise from 'debounce-promise';
import { InputField } from '@kiwicom/orbit-components';
import type { TranslationType } from '@kiwicom/account-consts';

import Text from '../Text';
import Tag from './Tag';

const List = styled.ul`
  list-style: none;
  background-color: ${({ theme }) => theme.orbit.paletteWhite};
  box-shadow: ${({ theme }) => theme.orbit.elevationFixedBoxShadow};
  position: absolute;
  border-radius: ${({ theme }) => theme.orbit.borderRadius100};
  top: 100%;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.orbit.zIndexOnTheTop};
`;

type ItemStyledProps = {
  highlighted: boolean;
};

const Item = styled.li<ItemStyledProps>`
  padding: ${({ theme }) => theme.orbit.space300};
  background-color: ${({ theme, highlighted }) => highlighted && theme.orbit.paletteWhiteHover};
  outline: none;
  cursor: pointer;
`;
const Wrapper = styled.div`
  position: relative;
`;
export type ItemProps = {
  highlighted: boolean;
  item: Record<string, unknown> & {
    readonly value: string;
    readonly label: string;
  };
};
type Props = {
  readonly onChange: (arg0: { value: string; label: string }) => void;
  readonly onRemoveValue: (arg0: string) => void;
  readonly loadOptions: (arg0: string) => Promise<void>;
  readonly options: readonly (
    | {
        readonly value: string;
        readonly label: string;
      }
    | null
    | undefined
  )[];
  readonly values: readonly (
    | {
        readonly value: string;
        readonly label: string;
      }
    | null
    | undefined
  )[];
  readonly accessibilityLabel?: string;
  readonly label?: TranslationType;
  readonly placeholder?: string;
  readonly help?: TranslationType;
  readonly showTags?: boolean;
  readonly debounceTime?: number;
  readonly focusOnFirstItem?: boolean;
  readonly renderItem?: (props: ItemProps) => JSX.Element;
};

const AutocompleteMultiple = ({
  onRemoveValue,
  onChange,
  loadOptions,
  options,
  values,
  accessibilityLabel,
  label,
  placeholder,
  help,
  showTags = false,
  debounceTime,
  focusOnFirstItem = false,
  renderItem,
}: Props): JSX.Element => {
  const [inputValue, setInputValue] = useState('');
  const [showList, setShowList] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(focusOnFirstItem ? 0 : null);
  const ignoreBlur = useRef(false);
  const orbitInputRef = useRef(null);
  const filteredOptions = useMemo(
    () => options.filter((option) => !values.some((value) => option?.value === value?.value)),
    [options, values],
  );
  useEffect(() => {
    const { current } = orbitInputRef;

    if (current != null) {
      // disable browser autocomplete
      current.setAttribute('autocomplete', 'off');
      // this ensures that the input is announced as combobox
      current.setAttribute('role', 'combobox');
      // this tells the user that the list of options will appear
      current.setAttribute('aria-autocomplete', 'list');
      current.setAttribute('autocapitalize', 'none');
      current.setAttribute('aria-owns', 'itemlist');
      current.setAttribute('aria-haspopup', 'listbox');
    }
  }, []);
  useEffect(() => {
    const { current } = orbitInputRef;

    if (current != null) {
      current.setAttribute('aria-expanded', showList.toString());
    }
  }, [showList]);
  useEffect(() => {
    const input = orbitInputRef.current;

    if (input != null) {
      // every time the highlighted index changes, we need to update the aria-activedescendant for the item to be announced on the screenreaders
      if (highlightedIndex != null && filteredOptions.length > 0) {
        const option = filteredOptions[highlightedIndex];
        const value = option?.value;
        input.setAttribute('aria-activedescendant', value);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }
  }, [highlightedIndex, filteredOptions]);

  const handleOnValuesChange = (item) => {
    onChange(item);
  };

  const onHighlightedIndexChange = (index) => {
    setHighlightedIndex(index);
  };

  const setIgnoreBlur = (value: boolean) => {
    ignoreBlur.current = value;
  };

  const resetHighlightedIndex = () => {
    setHighlightedIndex(focusOnFirstItem ? 0 : null);
  };

  const handleArrowDown = (e) => {
    e.preventDefault();

    if (filteredOptions.length === 0) {
      return;
    }

    if (highlightedIndex === null || highlightedIndex === filteredOptions.length - 1) {
      onHighlightedIndexChange(0);
    } else {
      onHighlightedIndexChange(highlightedIndex + 1);
    }
  };

  const handleArrowUp = (e) => {
    e.preventDefault();

    if (filteredOptions.length === 0) {
      return;
    }

    if (highlightedIndex === null || highlightedIndex === 0) {
      onHighlightedIndexChange(filteredOptions.length - 1);
    } else {
      onHighlightedIndexChange(highlightedIndex - 1);
    }
  };

  const handleEnter = (e) => {
    e.preventDefault();
    const selectedItem = highlightedIndex != null ? filteredOptions[highlightedIndex] : null;

    if (selectedItem != null) {
      setInputValue('');
      resetHighlightedIndex();
      setShowList(false);
      handleOnValuesChange(selectedItem);
    }
  };

  const handleEscape = () => {
    // in case the user is hovering over the option list
    setIgnoreBlur(false);
    resetHighlightedIndex();
    setShowList(false);
  };

  const handleBackspace = () => {
    if (inputValue === '' && showTags) {
      // allows user to delete the Tag with backspace
      onRemoveValue(values[values.length - 1]?.value ?? '');
    }
  };

  const handleTab = () => {
    // in case the user is hovering over the option list
    setIgnoreBlur(false);
    resetHighlightedIndex();
  };

  const handleInputKeyDown = (e) => {
    const { key } = e;

    switch (key) {
      case 'ArrowDown':
        handleArrowDown(e);
        break;

      case 'ArrowUp':
        handleArrowUp(e);
        break;

      case 'Enter':
        handleEnter(e);
        break;

      case 'Escape':
        handleEscape();
        break;

      case 'Tab':
        handleTab();
        break;

      case 'Backspace':
        handleBackspace();
        resetHighlightedIndex();
        break;

      default:
        if (!showList) {
          setShowList(true);
          resetHighlightedIndex();
        }
    }
  };

  const loadOptionsDebounce = useMemo(
    () =>
      debouncePromise((value) => {
        loadOptions(value);
      }, debounceTime),
    [loadOptions, debounceTime],
  );

  const onInputChange = (e) => {
    setInputValue(e.target.value);

    if (debounceTime == null) {
      loadOptions(inputValue);
    } else {
      loadOptionsDebounce(e.target.value);
    }

    if (e.target.value === '') {
      resetHighlightedIndex();
      setShowList(false);
    }
  };

  const onFocus = () => {
    setShowList(true);
  };

  const onBlur = () => {
    if (ignoreBlur.current) {
      return;
    }

    resetHighlightedIndex();
    setShowList(false);
  };

  const handleListItemClick = (item) => {
    setIgnoreBlur(false);
    handleOnValuesChange(item);
    resetHighlightedIndex();
    setShowList(false);
    setInputValue('');
  };

  const handleItemMouseEnter = (index: number) => {
    onHighlightedIndexChange(index);
  };

  return (
    <Wrapper>
      <InputField
        tags={
          showTags &&
          values.length > 0 && (
            <>
              {values.map((value, index) => (
                <Tag
                  onRemove={onRemoveValue}
                  value={value?.value ?? ''}
                  label={value?.label ?? ''}
                  key={value?.value ?? ''}
                  isLast={index === values.length - 1}
                />
              ))}
            </>
          )
        }
        ref={orbitInputRef}
        label={label}
        value={inputValue}
        onChange={onInputChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        help={help}
        dataTest="AutocompleteInput"
      />
      {showList && (
        <List // in order to not close the list when clicking on it we need to ignore the onBlur
          onMouseEnter={() => setIgnoreBlur(true)}
          onMouseLeave={() => setIgnoreBlur(false)}
          aria-label={accessibilityLabel}
          role="listbox"
          id="itemlist"
          data-test="autocomplete-list"
        >
          {filteredOptions.map((i, index) => {
            const itemProps = {
              role: 'option',
              tabIndex: -1,
              key: i?.value ?? '',
              id: i?.value ?? '',
              'aria-selected': index === highlightedIndex,
              'aria-posinset': index + 1,
              'aria-setsize': filteredOptions.length,
              onMouseEnter: () => handleItemMouseEnter(index),
              onMouseDown: () => handleListItemClick(i),
              'data-test': `option-${i?.value ?? ''}`,
            };
            // we want to be able to pass a custom item to the autocomplete
            if (renderItem) {
              if (i) {
                return (
                  <li key={itemProps.key} {...itemProps}>
                    {renderItem({
                      item: i,
                      highlighted: index === highlightedIndex,
                    })}
                  </li>
                );
              }
            }

            return (
              <Item key={itemProps.id} {...itemProps} highlighted={index === highlightedIndex}>
                <Text>{i?.label}</Text>
              </Item>
            );
          })}
        </List>
      )}
    </Wrapper>
  );
};

export default AutocompleteMultiple;
